import { useEffect, useMemo, useReducer, useRef } from 'react';
import { addError } from '@shared/utils/logger';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { AutopopulateJobStatusV5, Maybe, useAutopopulateOperationsByEventIdQuery } from '@graphql/generated';
import { usePolling } from '@shared/hooks/usePolling';
import type { AutoPopulationPollingStatus, AutoPopulationPollingResult } from '../types';

const POLL_SCHEDULE_MS = [5000, 10_000, 20_000, 40_000, 80_000, 120_000, 180_000];

interface UseAutoPopulationPollingOptions
  extends Readonly<{
    eventId?: Maybe<string>;
    shouldSkipAutoPopulationCheck: boolean;
    context: string;
  }> {}

interface UseAutoPopulationPollingReturn
  extends Readonly<{
    status: AutoPopulationPollingStatus;
    results: ReadonlyArray<AutoPopulationPollingResult>;
    count: number;
    loading: boolean;
    error: Maybe<string>;
    restartPolling: () => void;
  }> {}

interface State
  extends Readonly<{
    status: AutoPopulationPollingStatus;
    results: ReadonlyArray<AutoPopulationPollingResult>;
    loading: boolean;
    error: Maybe<string>;
  }> {}

type OperationResult = ReadonlyArray<{
  accommodationPlaceId: string;
  whyThisHotelIsAGreatPick?: Maybe<string>;
  proposedNoteFromHostToGuest?: Maybe<string>;
}> | null;

type Operation = Readonly<{
  status: AutopopulateJobStatusV5;
  result?: OperationResult;
}>;

interface AutoPopulationProcessedData
  extends Readonly<{
    status: AutopopulateJobStatusV5;
    results: ReadonlyArray<AutoPopulationPollingResult>;
  }> {}

type Action = Readonly<{ type: 'POLL_START' } | { type: 'UPDATE'; payload: Partial<State> } | { type: 'RESET' }>;

const initialState: State = {
  status: 'IDLE',
  results: [],
  loading: false,
  error: null,
} as const;

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'POLL_START':
      return { status: 'PENDING', results: [], loading: true, error: null };
    case 'UPDATE':
      return { ...initialState, ...action.payload };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
};

/**
 * Processes operations data to determine status and collect results.
 * Returns PENDING if any operation is pending, otherwise COMPLETED with results.
 * @param operations - Array of operations to process
 * @returns Processed data with status and results
 */
const autoPopulationProcessedData = (operations: ReadonlyArray<Operation> | null | undefined): AutoPopulationProcessedData => {
  // Check if any operation is PENDING
  const hasPendingOperation = operations?.some(operation => operation.status === AutopopulateJobStatusV5.PENDING) ?? false;

  if (hasPendingOperation) {
    return { status: AutopopulateJobStatusV5.PENDING, results: [] };
  }

  // Collect results from COMPLETED operations only
  const allResults =
    operations
      ?.filter(operation => operation.status === AutopopulateJobStatusV5.COMPLETED)
      .flatMap(operation => operation.result ?? [])
      .map(place => ({
        accommodationPlaceId: place.accommodationPlaceId,
        whyThisHotelIsAGreatPick: place.whyThisHotelIsAGreatPick ?? '',
        proposedNoteFromHostToGuest: place.proposedNoteFromHostToGuest ?? '',
      })) ?? [];

  return {
    status: AutopopulateJobStatusV5.COMPLETED,
    results: allResults,
  };
};

const logError = (message: string, context: string, eventId?: Maybe<string>) => {
  addError(new Error(message), {
    context,
    eventId,
    isAsync: true,
  });
};

/**
 * Hook to poll auto-populate accommodation operations status.
 */
export const useAutoPopulationPolling = (options: UseAutoPopulationPollingOptions): UseAutoPopulationPollingReturn => {
  const { eventId, shouldSkipAutoPopulationCheck, context } = options;

  const [state, dispatch] = useReducer(reducer, initialState);
  const hasCompletedRef = useRef(false);
  const isMountedRef = useRef(true);
  const hasMaxAttemptsLoggedRef = useRef(false);

  const {
    data,
    error: apiError,
    refetch,
    loading: queryLoading,
  } = useAutopopulateOperationsByEventIdQuery({
    variables: { payload: { eventId: eventId! } },
    skip: !eventId || shouldSkipAutoPopulationCheck,
    batchMode: 'fast',
  });

  // Reset on eventId change
  useEffect(() => {
    dispatch({ type: 'RESET' });
    hasCompletedRef.current = false;
    hasMaxAttemptsLoggedRef.current = false;
  }, [eventId]);

  // Track mount status
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Process operations data when query data changes
  const processedData = useMemo(() => autoPopulationProcessedData(data?.autopopulateOperationsByEventId?.operations ?? []), [data]);

  // Process and update state from query data
  useEffect(() => {
    if (!eventId || shouldSkipAutoPopulationCheck || !isMountedRef.current) return;

    // Handle errors
    if (apiError) {
      const errorMessage = apiError.message || 'Auto-population failed';
      dispatch({ type: 'UPDATE', payload: { status: 'FAILED', error: errorMessage } });
      logError(errorMessage, context, eventId);
      return;
    }

    // Handle operations status
    switch (processedData.status) {
      case AutopopulateJobStatusV5.PENDING:
        dispatch({ type: 'UPDATE', payload: { status: 'PENDING', loading: true } });
        return;
      case AutopopulateJobStatusV5.COMPLETED:
        dispatch({ type: 'UPDATE', payload: { status: 'COMPLETED', results: processedData.results } });
        break;
    }
  }, [data, apiError, eventId, shouldSkipAutoPopulationCheck, processedData, context]);

  useEffect(() => {
    if (state.status !== 'COMPLETED' || hasCompletedRef.current || !eventId || !isMountedRef.current) return;
    hasCompletedRef.current = true;
  }, [state.status, eventId]);

  // Determine if polling should continue
  const shouldPoll = useEventCallback((pollCount: number) => {
    if (!eventId || shouldSkipAutoPopulationCheck || !isMountedRef.current) return false;

    // Stop polling after 7 attempts and log error once
    if (pollCount >= 7) {
      if (!hasMaxAttemptsLoggedRef.current && isMountedRef.current) {
        hasMaxAttemptsLoggedRef.current = true;
        const errorMessage = 'Auto-population polling stopped after maximum attempts (7)';
        dispatch({ type: 'UPDATE', payload: { status: 'FAILED', error: errorMessage } });
        logError(errorMessage, context, eventId);
      }
      return false;
    }

    // Continue polling if query is still loading to avoid stopping prematurely
    // when data hasn't loaded yet (e.g., after page refresh)
    if (queryLoading) {
      return true;
    }

    const isProcessedDataCompleted = ['COMPLETED', 'FAILED'].includes(processedData?.status);
    const isStateCompleted = ['COMPLETED', 'FAILED'].includes(state?.status);
    const isOperationsCompleted = isProcessedDataCompleted || isStateCompleted;
    return !isOperationsCompleted;
  });

  // Poll for operations status
  const onPoll = useEventCallback(async () => {
    dispatch({ type: 'POLL_START' });
    await refetch();
  });

  // Handle polling error
  const onPollError = useEventCallback((error: unknown) => {
    if (!isMountedRef.current) return;
    const errorMessage = error instanceof Error ? error.message : 'Polling error occurred';
    dispatch({ type: 'UPDATE', payload: { status: 'FAILED', error: errorMessage } });
    logError(errorMessage, context, eventId);
  });

  // Handle polling timeout
  const onPollTimeout = useEventCallback(() => {
    if (!isMountedRef.current) return;
    dispatch({ type: 'UPDATE', payload: { status: 'FAILED', error: 'Polling timeout' } });
    logError('Auto-population polling timeout', context, eventId);
  });

  // Poll for operations status
  const { restart } = usePolling({
    shouldPoll,
    onPoll,
    onError: onPollError,
    onTimeout: onPollTimeout,
    pollScheduleMs: POLL_SCHEDULE_MS,
    immediate: true,
  });

  // Reset state when restarting polling
  const restartPolling = useEventCallback(async () => {
    if (!eventId || shouldSkipAutoPopulationCheck || !isMountedRef.current) return;

    dispatch({ type: 'RESET' });
    hasCompletedRef.current = false;
    hasMaxAttemptsLoggedRef.current = false;

    await refetch();
    restart();
  });

  return {
    status: state.status,
    results: state.results,
    count: state.results.length,
    loading: state.loading || queryLoading,
    error: state.error,
    restartPolling,
  };
};
